import React, { useState, useRef, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, MapPin } from "lucide-react";
import {
  GoogleMap,
  useLoadScript,
  MarkerF,
  OverlayViewF,
} from "@react-google-maps/api";
import "./LocationSelectionPage.css";
import progressIcon from "../../assets/progressIcon1.png";
import nextIcon from "../../assets/nextButtonIcon.png";
import MobileTopBar from "../../components/MobileTopBar";
import MobileBottomBar from "../../components/MobileBottomBar";
import DesktopTopBar from "../../components/DesktopTopBar";
import DesktopBottomBar from "../../components/DesktopBottomBar";
import { useGlobalAlert } from "../../context/AlertContext";

import apiClient from "../../api/apiClient";
import { ENDPOINTS } from "../../api/endPoints";
import dogPlaceholder from "../../assets/dogPlaceholder.png";
import userPlaceholder from "../../assets/userPlaceholder.png";
import pawPrintIcon from "../../assets/pawIcon.svg";

import { useSelector } from "react-redux";

const libraries = ["places"];
const fallbackCenter = { lat: 40.7648, lng: -73.9808 }; // Default: NYC

const interactiveMapOptions = {
  disableDefaultUI: true,
  zoomControl: true,
  gestureHandling: "auto",
  mapTypeControl: false,
  streetViewControl: false,
};

const backgroundMapOptions = {
  disableDefaultUI: true,
  gestureHandling: "greedy",
  zoomControl: true,
  scrollwheel: true,
  disableDoubleClickZoom: false,
  draggable: true,
  styles: [
    {
      featureType: "poi.park",
      elementType: "geometry",
      stylers: [{ color: "#e5f5e0" }],
    },
    {
      featureType: "poi",
      elementType: "labels.icon",
      stylers: [{ visibility: "off" }],
    },
    {
      featureType: "poi",
      elementType: "labels.text",
      stylers: [{ visibility: "off" }],
    },
    {
      featureType: "road",
      elementType: "labels",
      stylers: [{ visibility: "on" }],
    },
    {
      featureType: "transit",
      elementType: "all",
      stylers: [{ visibility: "off" }],
    },
  ],
};

const LocationSelectionPage = ({ formData, setFormData }) => {
  const navigate = useNavigate();
  const { triggerAlert } = useGlobalAlert();
  const { profile } = useSelector((state) => state.user);

  const addressDebounceRef = useRef(null);
  const isFirstRender = useRef(true);

  const [bgMapRef, setBgMapRef] = useState(null);
  const [pinMapRef, setPinMapRef] = useState(null);
  const [showLocationPrompt, setShowLocationPrompt] = useState(false);

  const isSocialAuth =
    formData.isSocialAuth === true || profile?.is_social_signup === 1;

  useEffect(() => {
    if (profile?.is_social_signup === 1 && !formData.email) {
      setFormData((prev) => ({
        ...prev,
        name: profile.user_name || prev.name,
        email: profile.user_email || profile.email || prev.email,
        isSocialAuth: true,
      }));
    }
  }, [profile, formData.email, setFormData]);

  const [mapCenter, setMapCenter] = useState(() => {
    const saved = localStorage.getItem("onboarding_map_center");
    return saved ? JSON.parse(saved) : fallbackCenter;
  });

  const [markerPosition, setMarkerPosition] = useState(() => {
    const saved = localStorage.getItem("onboarding_marker_pos");
    return saved ? JSON.parse(saved) : null;
  });

  const [pinnedAddressLabel, setPinnedAddressLabel] = useState(() => {
    return localStorage.getItem("onboarding_pinned_label") || "";
  });

  const [isFetchingLocation, setIsFetchingLocation] = useState(false);
  const [nearbyDogs, setNearbyDogs] = useState([]);
  const [selectedDogId, setSelectedDogId] = useState(null);

  const [isVerificationSent, setIsVerificationSent] = useState(() => {
    return localStorage.getItem("sp_verification_sent") === "true";
  });
  const [isVerifying, setIsVerifying] = useState(false);

  useEffect(() => {
    localStorage.setItem("sp_verification_sent", isVerificationSent);
  }, [isVerificationSent]);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    setIsVerificationSent(false);
    localStorage.setItem("sp_verification_sent", "false");
  }, [formData.email]);

  useEffect(() => {
    const channel = new BroadcastChannel("sp_verification_channel");
    channel.onmessage = (event) => {
      if (event.data === "email_verified") {
        triggerAlert("Email verified successfully!", "success");
        setIsVerifying(false);
        setIsVerificationSent(true);
        localStorage.setItem("sp_verification_sent", "true");
        navigate("/team-selection");
      }
    };
    return () => channel.close();
  }, [navigate, triggerAlert]);

  const { isLoaded } = useLoadScript({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
    libraries,
  });

  useEffect(() => {
    if (bgMapRef && mapCenter) bgMapRef.panTo(mapCenter);
  }, [bgMapRef, mapCenter]);

  useEffect(() => {
    if (pinMapRef && mapCenter) pinMapRef.panTo(mapCenter);
  }, [pinMapRef, mapCenter]);

  const fetchNearbyDogs = useCallback(async (lat, lng) => {
    try {
      const response = await apiClient.post(ENDPOINTS.GET_NEARBY_DOGS, {
        latitude: lat,
        longitude: lng,
      });
      if (response.data?.status) setNearbyDogs(response.data.dogs || []);
    } catch (err) {
      console.error("Map Preview Load Error:", err);
    }
  }, []);

  useEffect(() => {
    if (mapCenter) fetchNearbyDogs(mapCenter.lat, mapCenter.lng);
  }, [mapCenter, fetchNearbyDogs]);

  // Initial Location Access Prompt
  useEffect(() => {
    const savedCenter = localStorage.getItem("onboarding_map_center");
    if (!savedCenter) {
      setShowLocationPrompt(true);
    }
  }, []);

  const parseAddressComponents = (components, formattedAddress = "") => {
    let streetNum = "",
      route = "",
      city = "",
      state = "",
      zip = "",
      country = "",
      neighborhood = "";

    components.forEach((component) => {
      const types = component.types;

      // Extract specific Neighborhood component
      if (types.includes("neighborhood")) {
        neighborhood = component.long_name;
      }

      if (types.includes("street_number")) streetNum = component.long_name;
      if (types.includes("route")) route = component.long_name;
      if (
        // types.includes("locality") ||
        types.includes("sublocality") ||
        types.includes("administrative_area_level_2")
      ) {
        if (!city) city = component.long_name;
      }
      if (types.includes("administrative_area_level_1"))
        state = component.short_name;
      if (types.includes("postal_code")) zip = component.long_name;
      if (types.includes("country")) country = component.long_name;
    });

    let street = `${streetNum} ${route}`.trim();
    if (!street && formattedAddress) {
      street = formattedAddress.split(",")[0].trim();
    }

    return { street, city, state, zip, country, neighborhood };
  };

  const fetchNeighborhoodWithFallback = useCallback(
    async (lat, lng, googleNeighborhood) => {
      try {
        const response = await apiClient.post(
          "/openAI/get-neighborhood-by-AI",
          {
            latitude: parseFloat(lat),
            longitude: parseFloat(lng),
          },
        );

        if (response.data?.status && response.data?.data?.neighborhood) {
          const aiNeigh = response.data.data.neighborhood;
          const lower = aiNeigh.toLowerCase();

          if (!lower.includes("sorry") && !lower.includes("not recognizable")) {
            return aiNeigh;
          }
        }
      } catch (err) {
        console.error("AI neighborhood fetch failed", err);
      }
      return googleNeighborhood;
    },
    [],
  );

  useEffect(() => {
    const savedCenter = localStorage.getItem("onboarding_map_center");

    if (isLoaded && savedCenter && !formData.street) {
      try {
        const parsedCenter = JSON.parse(savedCenter);
        const geocoder = new window.google.maps.Geocoder();

        geocoder.geocode({ location: parsedCenter }, (results, status) => {
          if (status === "OK" && results[0]) {
            const fullAddress = results[0].formatted_address;
            const parsedAddress = parseAddressComponents(
              results[0].address_components,
              fullAddress,
            );

            fetchNeighborhoodWithFallback(
              parsedCenter.lat,
              parsedCenter.lng,
              parsedAddress.neighborhood,
            ).then((finalNeighborhood) => {
              setFormData((prev) => {
                if (!prev.street) {
                  return {
                    ...prev,
                    street: parsedAddress.street || "",
                    city: parsedAddress.city || "",
                    state: parsedAddress.state || "",
                    zip: parsedAddress.zip || "",
                    country: parsedAddress.country || "",
                    neighborhood: finalNeighborhood || "",
                    user_latitude: parsedCenter.lat.toString(),
                    user_longitude: parsedCenter.lng.toString(),
                  };
                }
                return prev;
              });
            });
          }
        });
      } catch (err) {
        console.warn("Failed to parse saved map center", err);
      }
    }
  }, [isLoaded, fetchNeighborhoodWithFallback, formData.street, setFormData]);

  const fetchCoordsFromAddress = useCallback(
    (address, shouldAutofill = true) => {
      if (!window.google || !address.trim()) return;

      setIsFetchingLocation(true);
      const geocoder = new window.google.maps.Geocoder();

      geocoder.geocode({ address: address }, (results, status) => {
        if (status === "OK" && results[0]) {
          const location = results[0].geometry.location;
          const lat = location.lat();
          const lng = location.lng();

          setMapCenter({ lat, lng });
          setMarkerPosition({ lat, lng });
          localStorage.setItem(
            "onboarding_map_center",
            JSON.stringify({ lat, lng }),
          );
          localStorage.setItem(
            "onboarding_marker_pos",
            JSON.stringify({ lat, lng }),
          );

          const fullAddress = results[0].formatted_address;
          setPinnedAddressLabel(fullAddress);
          localStorage.setItem("onboarding_pinned_label", fullAddress);

          if (shouldAutofill) {
            const parsedAddress = parseAddressComponents(
              results[0].address_components,
              fullAddress,
            );

            fetchNeighborhoodWithFallback(
              lat,
              lng,
              parsedAddress.neighborhood,
            ).then((finalNeighborhood) => {
              setFormData((prev) => ({
                ...prev,
                street: parsedAddress.street || "",
                city: parsedAddress.city || "",
                state: parsedAddress.state || "",
                zip: parsedAddress.zip || "",
                country: parsedAddress.country || "",
                neighborhood: finalNeighborhood || "",
                user_latitude: lat.toString(),
                user_longitude: lng.toString(),
              }));
              setIsFetchingLocation(false);
            });
          } else {
            setIsFetchingLocation(false);
          }
        } else {
          console.error("Geocoder failed due to: " + status);
          if (shouldAutofill) {
            triggerAlert(
              "Could not find that address. Please try again.",
              "error",
            );
          }
          setIsFetchingLocation(false);
        }
      });
    },
    [setFormData, triggerAlert, fetchNeighborhoodWithFallback],
  );

  const handleGetCurrentLocation = () => {
    setShowLocationPrompt(false);
    if ("geolocation" in navigator) {
      setIsFetchingLocation(true);
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const lat = position.coords.latitude;
          const lng = position.coords.longitude;

          setMapCenter({ lat, lng });
          localStorage.setItem(
            "onboarding_map_center",
            JSON.stringify({ lat, lng }),
          );

          setMarkerPosition({ lat, lng });
          localStorage.setItem(
            "onboarding_marker_pos",
            JSON.stringify({ lat, lng }),
          );

          const geocoder = new window.google.maps.Geocoder();
          geocoder.geocode({ location: { lat, lng } }, (results, status) => {
            if (status === "OK" && results[0]) {
              const fullAddress = results[0].formatted_address;
              const parsedAddress = parseAddressComponents(
                results[0].address_components,
                fullAddress,
              );

              fetchNeighborhoodWithFallback(
                lat,
                lng,
                parsedAddress.neighborhood,
              ).then((finalNeighborhood) => {
                setFormData((prev) => ({
                  ...prev,
                  street: parsedAddress.street || "",
                  city: parsedAddress.city || "",
                  state: parsedAddress.state || "",
                  zip: parsedAddress.zip || "",
                  country: parsedAddress.country || "",
                  neighborhood: finalNeighborhood || "",
                  user_latitude: lat.toString(),
                  user_longitude: lng.toString(),
                }));

                setPinnedAddressLabel(fullAddress);
                localStorage.setItem("onboarding_pinned_label", fullAddress);
                setIsFetchingLocation(false);
              });
            } else {
              setIsFetchingLocation(false);
            }
          });
        },
        (error) => {
          console.error("Error getting location", error);
          setIsFetchingLocation(false);
          setMapCenter(fallbackCenter);
        },
        { enableHighAccuracy: true, timeout: 8000, maximumAge: 10000 },
      );
    } else {
      triggerAlert("Geolocation is not supported by your browser", "error");
    }
  };

  const handleMapClick = (event) => {
    const lat = event.latLng.lat();
    const lng = event.latLng.lng();

    setMarkerPosition({ lat, lng });
    localStorage.setItem("onboarding_marker_pos", JSON.stringify({ lat, lng }));

    setMapCenter({ lat, lng });
    localStorage.setItem("onboarding_map_center", JSON.stringify({ lat, lng }));

    const geocoder = new window.google.maps.Geocoder();
    geocoder.geocode({ location: { lat, lng } }, (results, status) => {
      if (status === "OK" && results[0]) {
        const fullAddress = results[0].formatted_address;
        setPinnedAddressLabel(fullAddress);
        localStorage.setItem("onboarding_pinned_label", fullAddress);

        const parsedAddress = parseAddressComponents(
          results[0].address_components,
          fullAddress,
        );

        fetchNeighborhoodWithFallback(
          lat,
          lng,
          parsedAddress.neighborhood,
        ).then((finalNeighborhood) => {
          setFormData((prev) => ({
            ...prev,
            street: parsedAddress.street || "",
            city: parsedAddress.city || "",
            state: parsedAddress.state || "",
            zip: parsedAddress.zip || "",
            country: parsedAddress.country || "",
            neighborhood: finalNeighborhood || "",
            user_latitude: lat.toString(),
            user_longitude: lng.toString(),
          }));
        });
      }
    });
  };

  const handlePinnedAddressChange = (value) => {
    let cleanValue = value.replace(/^ADDRESS:\s*/i, "");
    setPinnedAddressLabel(cleanValue);

    if (addressDebounceRef.current) {
      clearTimeout(addressDebounceRef.current);
    }

    if (!cleanValue.trim()) {
      setFormData((prev) => ({
        ...prev,
        street: "",
        city: "",
        state: "",
        zip: "",
        country: "",
        neighborhood: "",
        apartment: "",
        user_latitude: "",
        user_longitude: "",
      }));
      return;
    }

    addressDebounceRef.current = setTimeout(() => {
      if (cleanValue.trim().length >= 8) {
        fetchCoordsFromAddress(cleanValue, true);
      }
    }, 1500);
  };

  useEffect(() => {
    return () => {
      if (addressDebounceRef.current) {
        clearTimeout(addressDebounceRef.current);
      }
    };
  }, []);

  const isAddressValid =
    (pinnedAddressLabel && pinnedAddressLabel.length > 3) ||
    (formData.street && formData.city);

  const isFormValid = isAddressValid;

  const executeNextStep = async () => {
    console.log("Submit Data HalfOnboarding:", formData);
    if (isSocialAuth) {
      navigate("/team-selection");
      return;
    }

    setIsVerifying(true);
    try {
      if (!isVerificationSent) {
        await apiClient.post("/emailVerification/send-verification", {
          email: formData.email,
        });

        triggerAlert(
          "Verification email sent! Please check your inbox and verify your email.",
          "success",
        );
        setIsVerificationSent(true);
      } else {
        const response = await apiClient.get(
          `/emailVerification/check-verification?email=${encodeURIComponent(
            formData.email,
          )}`,
        );
        const { status, is_verified } = response.data || {};

        if (status === true && is_verified === true) {
          navigate("/team-selection");
        } else {
          triggerAlert("Please verify your email address.", "error");
        }
      }
    } catch (error) {
      console.error("Verification API Error:", error);
      if (isVerificationSent) {
        triggerAlert("Please verify your email address.", "error");
      } else {
        triggerAlert(
          error.response?.data?.message ||
            "Failed to send verification email. Please try again.",
          "error",
        );
      }
    } finally {
      setIsVerifying(false);
    }
  };

  const handleNext = async () => {
    if (!isFormValid || isVerifying) return;

    if (!formData.street || !formData.city) {
      setIsVerifying(true);

      if (window.google && pinnedAddressLabel) {
        const geocoder = new window.google.maps.Geocoder();
        geocoder.geocode({ address: pinnedAddressLabel }, (results, status) => {
          if (status === "OK" && results[0]) {
            const location = results[0].geometry.location;
            const lat = location.lat();
            const lng = location.lng();

            const fullAddress = results[0].formatted_address;
            const parsedAddress = parseAddressComponents(
              results[0].address_components,
              fullAddress,
            );

            fetchNeighborhoodWithFallback(
              lat,
              lng,
              parsedAddress.neighborhood,
            ).then((finalNeighborhood) => {
              setFormData((prev) => ({
                ...prev,
                street: parsedAddress.street || "",
                city: parsedAddress.city || "",
                state: parsedAddress.state || "",
                zip: parsedAddress.zip || "",
                country: parsedAddress.country || "",
                neighborhood: finalNeighborhood || "",
                user_latitude: lat.toString(),
                user_longitude: lng.toString(),
              }));
              executeNextStep();
            });
          } else {
            triggerAlert(
              "Please allow the address to finish loading.",
              "error",
            );
            setIsVerifying(false);
          }
        });
      } else {
        setIsVerifying(false);
      }
      return;
    }
    executeNextStep();
  };

  const handleBackNavigation = () => {
    setFormData((prev) => ({
      ...prev,
      name: "",
      email: "",
      password: "",
      confirmPassword: "",
      street: "",
      apartment: "",
      city: "",
      state: "",
      zip: "",
      neighborhood: "",
      isSocialAuth: false,
    }));
    navigate("/");
  };

  return (
    <div className="sp-container">
      <MobileTopBar
        onBack={handleBackNavigation}
        backColor="#fff"
        logoColor="#fff"
        isProtectedRoute={false}
      />

      {/* Map Section - Left on Desktop, Top on Mobile */}
      <div className="sp-onboarding-map-section">
        <div className="sp-map-container background-map">
          {isLoaded ? (
            <GoogleMap
              onLoad={(map) => setBgMapRef(map)}
              mapContainerStyle={{
                width: "100%",
                height: "100%",
                position: "absolute",
                top: 0,
                left: 0,
              }}
              center={mapCenter}
              zoom={14}
              options={backgroundMapOptions}
              onClick={() => setSelectedDogId(null)}
            >
              {(markerPosition || mapCenter) && (
                <OverlayViewF
                  position={markerPosition || mapCenter}
                  mapPaneName="overlayMouseTarget"
                >
                  <div
                    className="user-location-pin scale-07"
                    style={{ maxWidth: "none", minWidth: "100px" }}
                  >
                    <div className="pulse-ring" />
                    <MapPin
                      size={24}
                      color="rgba(0, 155, 206, 1)"
                      fill="white"
                    />
                  </div>
                </OverlayViewF>
              )}

              {nearbyDogs.map((dog) => {
                const isSelected = selectedDogId === dog.id;
                return (
                  <OverlayViewF
                    key={dog.id}
                    position={{
                      lat: parseFloat(dog.user_latitude),
                      lng: parseFloat(dog.user_longitude),
                    }}
                    mapPaneName="overlayMouseTarget"
                  >
                    <div
                      style={{
                        position: "relative",
                        zIndex: isSelected ? 100 : 1,
                      }}
                    >
                      <div
                        className={`onboadring-dog-pin ${isSelected ? "onboadring-pin-active" : ""}`}
                        style={{
                          transform: isSelected
                            ? "translate(-50%, -100%) scale(1.15)"
                            : "translate(-50%, -100%) scale(0.8)",
                          transition: "transform 0.25s ease-in-out",
                          cursor: "pointer",
                          boxShadow: isSelected
                            ? "0px 8px 16px rgba(0,0,0,0.3)"
                            : "none",
                          maxWidth: "none",
                          minWidth: "50px",
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedDogId(isSelected ? null : dog.id);
                        }}
                      >
                        <img
                          src={
                            (dog.team_social === 1
                              ? dog.profile_image?.[0] ||
                                dog.user_profile_image?.[0]
                              : dog.dog_images?.[0] ||
                                dog.profile_image?.[0]) ||
                            (dog.team_social === 1
                              ? userPlaceholder
                              : dogPlaceholder)
                          }
                          alt="dog"
                          style={{ maxWidth: "none" }}
                          onError={(e) => {
                            e.target.src =
                              dog.team_social === 1
                                ? userPlaceholder
                                : dogPlaceholder;
                          }}
                        />
                      </div>

                      {isSelected && (
                        <div
                          className="onboadring-dog-info-card top"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <div className="onboadring-card-main-row">
                            <div className="onboadring-map-info-card-dog-avatar">
                              <img
                                src={
                                  (dog.team_social === 1
                                    ? dog.profile_image?.[0] ||
                                      dog.user_profile_image?.[0]
                                    : dog.dog_images?.[0] ||
                                      dog.profile_image?.[0]) ||
                                  (dog.team_social === 1
                                    ? userPlaceholder
                                    : dogPlaceholder)
                                }
                                alt={dog.dog_name || "Dog"}
                                onError={(e) => {
                                  e.target.src =
                                    dog.team_social === 1
                                      ? userPlaceholder
                                      : dogPlaceholder;
                                }}
                                style={{ maxWidth: "none" }}
                              />
                            </div>
                            <div className="onboadring-card-right-column">
                              <h2 className="onboadring-card-dog-name">
                                {dog.team_social === 1
                                  ? dog.user_name
                                  : dog.dog_name}
                              </h2>
                              <div className="onboadring-card-distance-badge">
                                <span className="onboadring-paw-icon">
                                  <img
                                    src={pawPrintIcon}
                                    alt="paw"
                                    className="onboadring-distance-paw-icon"
                                    style={{ maxWidth: "none" }}
                                  />
                                </span>
                                {Math.round(dog.distance_miles * 20) <= 20
                                  ? `${Math.round(dog.distance_miles * 20)} blocks away`
                                  : `${Number(dog.distance_miles.toFixed(1))} miles away`}
                              </div>
                            </div>
                          </div>
                          <div className="onboadring-card-arrow" />
                        </div>
                      )}
                    </div>
                  </OverlayViewF>
                );
              })}
            </GoogleMap>
          ) : (
            <div className="map-loader-container">
              <Loader2 className="animate-spin" size={32} color="#009fd9" />
            </div>
          )}
        </div>
      </div>

      {/* Form Section - Right on Desktop, Bottom on Mobile */}
      <div className="sp-onboarding-form-section right-panel-blue">
        <DesktopTopBar
          showBack={true}
          onBack={handleBackNavigation}
          backColor="#fff"
          showProgress={true}
          progressIconSrc={progressIcon}
          progressPercent={0}
          iconLeft={10}
          iconWidth="90px"
        />

        <div className="sp-unified-form-content">
          <h1 className="sp-unified-title">LET'S GET STARTED!</h1>
          <p className="sp-unified-subtitle">
            Navigate the pin to where you are so we can display your
            neighborhood dogs - your address is never shared, not even to us!
          </p>

          <button
            className="sp-drop-pin-btn"
            onClick={() => setShowLocationPrompt(true)}
          >
            <MapPin size={20} />
            <span>Show us where you are!</span>
          </button>

          <div className="sp-interactive-map-card">
            {!isLoaded ? (
              <div className="map-loader-container">
                <Loader2 className="animate-spin" size={32} color="#009fd9" />
              </div>
            ) : (
              <GoogleMap
                onLoad={(map) => setPinMapRef(map)}
                mapContainerStyle={{
                  width: "100%",
                  height: "100%",
                  borderRadius: "1rem",
                }}
                center={mapCenter}
                zoom={14}
                options={interactiveMapOptions}
                onClick={handleMapClick}
              >
                {markerPosition && <MarkerF position={markerPosition} />}
              </GoogleMap>
            )}
          </div>

          <div className="sp-address-input-box">
            <span className="sp-address-prefix">ADDRESS:</span>
            {isFetchingLocation ? (
              <span className="sp-address-updating">Updating address...</span>
            ) : (
              <input
                type="text"
                className="sp-address-direct-input"
                value={pinnedAddressLabel}
                onChange={(e) => handlePinnedAddressChange(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    if (addressDebounceRef.current) {
                      clearTimeout(addressDebounceRef.current);
                    }
                    fetchCoordsFromAddress(pinnedAddressLabel, true);
                  }
                }}
                placeholder="10 Columbus Cir, New York, NY 10019"
              />
            )}
          </div>
        </div>
        <DesktopBottomBar
          onNext={handleNext}
          isNextEnabled={isFormValid}
          isLoading={isVerifying}
          nextIconSrc={nextIcon}
          variant="green"
        />

        <MobileBottomBar
          onNext={handleNext}
          isNextEnabled={isFormValid && !isVerifying}
          nextIconSrc={nextIcon}
          variant="green"
        />
      </div>

      {/* Location Access Prompt Modal */}
      {showLocationPrompt && (
        <div className="sp-location-modal-overlay">
          <div className="sp-location-modal-content">
            <div className="sp-location-modal-icon">
              <MapPin size={32} color="#009fd9" fill="white" />
            </div>
            <h2>Find Your Local Pack</h2>
            <p>Discover nearby pups and pup pals in your neighborhood</p>

            <button
              className="sp-modal-btn-primary"
              onClick={handleGetCurrentLocation}
            >
              ALWAYS ALLOW
            </button>
            <button
              className="sp-modal-btn-primary"
              onClick={handleGetCurrentLocation}
            >
              ALLOW WHILE USING APP
            </button>
            <button
              className="sp-modal-btn-secondary"
              onClick={() => setShowLocationPrompt(false)}
            >
              Don't Allow
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default LocationSelectionPage;
